import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Globe, TrendingUp, Users, Handshake, ArrowRight, MapPin, Send, Building2, Package, Target } from 'lucide-react';
import { toast } from 'sonner';
import { trackEvent } from '../analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForManufacturers = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    country: '',
    manufacturer_name: '',
    products_offered: '',
    target_markets: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/inquiries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          inquiry_type: 'partner',
          name: formData.name,
          email: formData.email,
          company: formData.company,
          phone: formData.phone,
          country: formData.country,
          manufacturer_name: formData.manufacturer_name,
          products_offered: formData.products_offered,
          target_markets: formData.target_markets,
          message: formData.message
        }),
      });

      if (response.ok) {
        toast.success('Thank you for your partnership inquiry! Our team will contact you shortly.');
        trackEvent('form_submit', { form_type: 'partner_inquiry' });
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          country: '',
          manufacturer_name: '',
          products_offered: '',
          target_markets: '',
          message: ''
        });
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: Globe,
      title: 'Market Access',
      description: 'Gain immediate access to Middle East, Africa, and Latin America markets through our established distribution network.'
    },
    {
      icon: Users,
      title: 'Local Expertise',
      description: 'Benefit from our deep understanding of regional market dynamics, regulations, and customer requirements.'
    },
    {
      icon: TrendingUp,
      title: 'Growth Partnership',
      description: 'Expand your market presence with a partner committed to driving your brand growth in emerging markets.'
    },
    {
      icon: Handshake,
      title: 'Trusted Representation',
      description: 'Professional representation of your brand with integrity, technical expertise, and customer service excellence.'
    }
  ];

  const markets = [
    {
      region: 'Middle East',
      countries: ['UAE', 'Saudi Arabia', 'Oman', 'Qatar', 'Kuwait', 'Bahrain'],
      highlight: 'Strategic hub location in Dubai'
    },
    {
      region: 'Africa',
      countries: ['Growing markets across the continent'],
      highlight: 'Expanding network and partnerships'
    },
    {
      region: 'Latin America',
      countries: ['Key markets in South & Central America'],
      highlight: 'Established supply chain connections'
    }
  ];

  return (
    <div>
      <SEO title="Partner With Us" description="Partner with Trustar Industrial Chemicals. We seek strategic distribution partnerships for TiO2 and specialty pigments across the Middle East, Africa, and Latin America." path="/partner-with-us" />
      {/* Hero Section */}
      <section className="relative min-h-[500px] bg-gradient-to-br from-indigo-700 via-blue-800 to-cyan-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 40%, rgba(255,255,255,0.2) 0%, transparent 50%),
                             radial-gradient(circle at 75% 70%, rgba(255,255,255,0.15) 0%, transparent 50%)`
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-4xl">
            <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Partner With Us for Market Expansion
            </h1>
            <p className="text-2xl text-blue-100 font-light mb-10 leading-relaxed">
              Are you a manufacturer seeking access to Middle East, Africa, or Latin America markets? 
              Let's explore how Trustar can be your gateway to growth.
            </p>
            <a href="#partner-form">
              <Button size="lg" variant="secondary" className="text-lg px-10 py-6">
                Discuss Partnership Opportunities <ArrowRight className="ml-2" size={22} />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Why Partner With Us */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">Why Partner With Trustar?</h2>
            <p className="text-xl text-gray-600">
              Strategic distribution partnership for market expansion
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl flex items-center justify-center mb-5">
                      <Icon className="text-primary" size={32} />
                    </div>
                    <h3 className="text-2xl font-bold mb-3">{benefit.title}</h3>
                    <p className="text-gray-600 leading-relaxed text-lg">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Market Coverage */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">Our Market Coverage</h2>
            <p className="text-xl text-gray-600">
              Established presence across three dynamic continents
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {markets.map((market, index) => (
              <Card key={index} className="border-none shadow-lg">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mb-5">
                    <MapPin className="text-primary" size={28} />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">{market.region}</h3>
                  <div className="mb-4">
                    {market.countries.map((country, idx) => (
                      <span key={idx} className="block text-gray-600 mb-1">{country}</span>
                    ))}
                  </div>
                  <div className="pt-4 border-t">
                    <p className="text-sm text-primary font-medium">{market.highlight}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="bg-blue-50 rounded-2xl p-10 text-center">
            <h3 className="text-2xl font-bold mb-4">Strategic Dubai Location</h3>
            <p className="text-lg text-gray-700 mb-2">
              Headquartered in Dubai South Business Park, we offer unparalleled access to GCC markets 
              with excellent connectivity to Africa and Latin America.
            </p>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">What We Offer Manufacturers</h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <h3 className="text-2xl font-bold mb-6">Distribution Services</h3>
              <ul className="space-y-4">
                {[
                  'Market penetration strategy and execution',
                  'Product positioning and brand building',
                  'Inventory management and warehousing',
                  'Logistics and supply chain optimization',
                  'Technical support and customer service',
                  'Market intelligence and feedback'
                ].map((service, index) => (
                  <li key={index} className="flex items-start">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <span className="text-lg text-gray-700">{service}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-2xl font-bold mb-6">Partnership Approach</h3>
              <ul className="space-y-4">
                {[
                  'Long-term strategic partnerships',
                  'Transparent communication and reporting',
                  'Joint marketing and promotion activities',
                  'Regular business reviews and planning',
                  'Dedicated account management',
                  'Flexible partnership models'
                ].map((approach, index) => (
                  <li key={index} className="flex items-start">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <span className="text-lg text-gray-700">{approach}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Inquiry Form */}
      <section id="partner-form" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">Start a Partnership Discussion</h2>
            <p className="text-xl text-gray-600">
              Tell us about your company and products. Our team will get in touch to explore partnership opportunities.
            </p>
          </div>

          <Card className="border-none shadow-xl">
            <CardContent className="p-8 lg:p-12">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="partner-form">
                {/* Contact Information */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <Users className="mr-2 text-primary" size={20} />
                    Contact Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        placeholder="Your name"
                        data-testid="partner-form-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        placeholder="your@email.com"
                        data-testid="partner-form-email"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="mt-1"
                        placeholder="+1 XXX XXX XXXX"
                        data-testid="partner-form-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="country">Country *</Label>
                      <Input
                        id="country"
                        name="country"
                        type="text"
                        value={formData.country}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        placeholder="Your country"
                        data-testid="partner-form-country"
                      />
                    </div>
                  </div>
                </div>

                {/* Company Information */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <Building2 className="mr-2 text-primary" size={20} />
                    Company Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="company">Your Company Name *</Label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        placeholder="Your company"
                        data-testid="partner-form-company"
                      />
                    </div>
                    <div>
                      <Label htmlFor="manufacturer_name">Manufacturer/Brand Name</Label>
                      <Input
                        id="manufacturer_name"
                        name="manufacturer_name"
                        type="text"
                        value={formData.manufacturer_name}
                        onChange={handleChange}
                        className="mt-1"
                        placeholder="Brand you represent"
                        data-testid="partner-form-manufacturer"
                      />
                    </div>
                  </div>
                </div>

                {/* Product & Market Information */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <Package className="mr-2 text-primary" size={20} />
                    Product & Market Information
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="products_offered">Products You Offer</Label>
                      <Textarea
                        id="products_offered"
                        name="products_offered"
                        value={formData.products_offered}
                        onChange={handleChange}
                        className="mt-1"
                        rows={3}
                        placeholder="Describe your product range (e.g., TiO2 grades, specialty pigments, applications...)"
                        data-testid="partner-form-products"
                      />
                    </div>
                    <div>
                      <Label htmlFor="target_markets" className="flex items-center">
                        <Target className="mr-1" size={14} />
                        Target Markets of Interest
                      </Label>
                      <Input
                        id="target_markets"
                        name="target_markets"
                        type="text"
                        value={formData.target_markets}
                        onChange={handleChange}
                        className="mt-1"
                        placeholder="e.g., GCC, Middle East, Africa, Latin America"
                        data-testid="partner-form-markets"
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Message */}
                <div className="mb-8">
                  <Label htmlFor="message">Additional Information</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="mt-1"
                    rows={4}
                    placeholder="Tell us more about your partnership goals and expectations..."
                    data-testid="partner-form-message"
                  />
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full md:w-auto"
                  disabled={isSubmitting}
                  data-testid="partner-form-submit"
                >
                  {isSubmitting ? (
                    <>Submitting...</>
                  ) : (
                    <>
                      <Send size={18} className="mr-2" />
                      Submit Partnership Inquiry
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-blue-600 to-indigo-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">Ready to Expand Your Market Reach?</h2>
          <p className="text-xl text-blue-100 mb-10 leading-relaxed">
            Let's discuss how Trustar can help you establish and grow your presence in Middle East, 
            Africa, and Latin America markets.
          </p>
          <Link to="/contact">
            <Button size="lg" variant="secondary" className="text-lg px-10 py-6">
              Contact Us for Partnership Discussion <ArrowRight className="ml-2" size={22} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ForManufacturers;
